import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, ExternalLink, Check, AlertCircle, Search, Sparkles, Edit2, MapPin } from 'lucide-react';
import { api } from '../../services/api';
import { ItemEditModal } from '../common/ItemEditModal';
import {
  EBAY_SITES,
  EBAY_CONDITIONS,
  EBAY_DURATIONS,
  type ItemWithImages,
  type ItemCondition,
  type EbaySiteId,
  type EbayConditionCode,
  type EbayListingType,
  type EbayDuration,
  type EbayCategory,
  type EbayItemAspect,
  type EbayCategoryCondition,
} from '@findmystuff/shared';

// Map app conditions to eBay condition codes
const CONDITION_MAP: Record<ItemCondition, EbayConditionCode> = {
  new: 1000,
  open_box: 1500,
  like_new: 3000,
  good: 4000,
  fair: 5000,
  poor: 6000,
};

interface SellModalProps {
  item: ItemWithImages;
  isOpen: boolean;
  onClose: () => void;
}

export function SellModal({ item, isOpen, onClose }: SellModalProps) {
  const queryClient = useQueryClient();

  // Form state
  const [siteId, setSiteId] = useState<EbaySiteId>('EBAY_US');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [condition, setCondition] = useState<EbayConditionCode>(4000);
  const [categoryId, setCategoryId] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [listingType, setListingType] = useState<EbayListingType>('AUCTION');
  const [startingPrice, setStartingPrice] = useState('0.99');
  const [buyItNowPrice, setBuyItNowPrice] = useState('');
  const [duration, setDuration] = useState<EbayDuration>('DAYS_7');

  // Category search
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Category-specific item specifics and conditions
  const [categoryAspects, setCategoryAspects] = useState<EbayItemAspect[]>([]);
  const [categoryConditions, setCategoryConditions] = useState<EbayCategoryCondition[]>([]);
  const [itemSpecifics, setItemSpecifics] = useState<Record<string, string>>({});
  const [isLoadingAspects, setIsLoadingAspects] = useState(false);

  // AI fill state
  const [useAiFill, setUseAiFill] = useState(true);
  const [isLoadingAiFill, setIsLoadingAiFill] = useState(false);

  // Success state
  const [listingResult, setListingResult] = useState<{ listingId: string; listingUrl: string } | null>(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // Initialize form with item data
  useEffect(() => {
    if (item) {
      setTitle(item.title.slice(0, 80));
      setDescription(item.description || '');
      setCondition(CONDITION_MAP[item.condition as ItemCondition] || 4000);
      setCategorySearch(item.category || '');
    }
  }, [item]);

  // Category search query
  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['ebay', 'categories', siteId, categorySearch],
    queryFn: () => api.ebay.searchCategories(siteId, categorySearch),
    enabled: categorySearch.length >= 2 && showCategoryDropdown,
    staleTime: 60000,
  });

  // Create listing mutation
  const createListingMutation = useMutation({
    mutationFn: () =>
      api.ebay.createListing({
        itemId: item.id,
        siteId,
        title,
        description,
        condition,
        categoryId,
        listingType,
        startingPrice: listingType === 'AUCTION' ? parseFloat(startingPrice) : undefined,
        buyItNowPrice: listingType === 'FIXED_PRICE' ? parseFloat(buyItNowPrice) : undefined,
        duration,
        itemSpecifics: Object.keys(itemSpecifics).length > 0 ? itemSpecifics : undefined,
      }),
    onSuccess: (data) => {
      setListingResult(data);
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const handleSelectCategory = async (cat: EbayCategory) => {
    setCategoryId(cat.categoryId);
    setCategoryName(cat.categoryName);
    setCategorySearch(cat.categoryName);
    setShowCategoryDropdown(false);

    // Fetch category-specific aspects and conditions
    setIsLoadingAspects(true);
    setCategoryAspects([]);
    setCategoryConditions([]);
    setItemSpecifics({});

    try {
      const categoryInfo = await api.ebay.getCategoryAspects(siteId, cat.categoryId);
      setCategoryAspects(categoryInfo.aspects);
      setCategoryConditions(categoryInfo.conditions);

      // Set condition to first valid option if current is not valid
      if (categoryInfo.conditions.length > 0) {
        const firstCondition = categoryInfo.conditions[0];
        const currentConditionValid = categoryInfo.conditions.some(c => c.conditionId === condition);
        if (!currentConditionValid && firstCondition) {
          // Try to map from item condition, or use first available
          const mappedCondition = CONDITION_MAP[item.condition as ItemCondition];
          const mappedValid = categoryInfo.conditions.some(c => c.conditionId === mappedCondition);
          setCondition(mappedValid ? mappedCondition : firstCondition.conditionId as EbayConditionCode);
        }
      }

      // Auto-fill from item.attributes (basic mapping)
      const autoFilled: Record<string, string> = {};
      const attributes = (item as any).attributes || {};

      for (const aspect of categoryInfo.aspects) {
        const aspectName = aspect.localizedAspectName;
        const lowerAspectName = aspectName.toLowerCase();

        // Try to match attribute keys (case-insensitive)
        for (const [key, value] of Object.entries(attributes)) {
          if (key.toLowerCase() === lowerAspectName && value) {
            autoFilled[aspectName] = String(value);
            break;
          }
        }

        // Common mappings
        if (!autoFilled[aspectName]) {
          if (lowerAspectName === 'brand' && attributes.brand) {
            autoFilled[aspectName] = String(attributes.brand);
          } else if (lowerAspectName === 'color' && attributes.color) {
            autoFilled[aspectName] = String(attributes.color);
          } else if (lowerAspectName === 'model' && attributes.model) {
            autoFilled[aspectName] = String(attributes.model);
          } else if (lowerAspectName === 'material' && attributes.material) {
            autoFilled[aspectName] = String(attributes.material);
          } else if (lowerAspectName === 'size' && attributes.size) {
            autoFilled[aspectName] = String(attributes.size);
          } else if (lowerAspectName === 'type' && item.category) {
            autoFilled[aspectName] = item.category;
          }
        }
      }

      setItemSpecifics(autoFilled);
      setIsLoadingAspects(false);

      // If AI fill is enabled, get AI suggestions
      if (useAiFill && item.images && item.images.length > 0) {
        setIsLoadingAiFill(true);
        try {
          const imageUrls = item.images.map(img => img.url);
          const suggestions = await api.ebay.getListingSuggestions(
            imageUrls,
            categoryInfo.aspects,
            item.title,
            item.description
          );

          // Apply AI suggestions to form fields
          if (suggestions.title) {
            setTitle(suggestions.title);
          }
          if (suggestions.description) {
            setDescription(suggestions.description);
          }
          if (suggestions.conditionId) {
            // Only apply if valid for this category
            const conditionValid = categoryInfo.conditions.some(c => c.conditionId === suggestions.conditionId);
            if (conditionValid) {
              setCondition(suggestions.conditionId as EbayConditionCode);
            }
          }
          if (suggestions.itemSpecifics) {
            setItemSpecifics(prev => {
              const updated = { ...prev };
              for (const [key, val] of Object.entries(suggestions.itemSpecifics!)) {
                // Only fill if not already filled
                if (!updated[key]) {
                  updated[key] = val.value;
                }
              }
              return updated;
            });
          }
        } catch (error) {
          console.error('Failed to get AI suggestions:', error);
        } finally {
          setIsLoadingAiFill(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch category info:', error);
      setIsLoadingAspects(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createListingMutation.mutate();
  };

  const handleClose = () => {
    setListingResult(null);
    onClose();
  };

  const getCurrency = () => {
    const site = EBAY_SITES.find((s) => s.id === siteId);
    return site?.currency || 'USD';
  };

  // Check if all required aspects are filled
  const requiredAspectsFilled = categoryAspects
    .filter((a) => a.aspectRequired)
    .every((a) => itemSpecifics[a.localizedAspectName]?.trim());

  const isValid =
    title.length >= 1 &&
    title.length <= 80 &&
    description.length >= 1 &&
    categoryId &&
    requiredAspectsFilled &&
    (listingType === 'AUCTION' ? parseFloat(startingPrice) > 0 : parseFloat(buyItNowPrice) > 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
            <h2 className="text-lg font-semibold">Sell on eBay</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium px-2 py-1"
              >
                <Edit2 className="w-4 h-4" />
                Edit Item
              </button>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {listingResult ? (
              // Success state
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Listed Successfully!</h3>
                <p className="text-gray-600 mb-6">
                  Your item has been listed on eBay.
                </p>
                <a
                  href={listingResult.listingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on eBay
                </a>
              </div>
            ) : (
              // Form
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Marketplace */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    eBay Marketplace
                  </label>
                  <select
                    value={siteId}
                    onChange={(e) => setSiteId(e.target.value as EbaySiteId)}
                    className="input"
                  >
                    {EBAY_SITES.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => {
                        setCategorySearch(e.target.value);
                        setShowCategoryDropdown(true);
                        if (!e.target.value) {
                          setCategoryId('');
                          setCategoryName('');
                          setCategoryAspects([]);
                          setCategoryConditions([]);
                          setItemSpecifics({});
                        }
                      }}
                      onFocus={() => setShowCategoryDropdown(true)}
                      placeholder="Search for a category..."
                      className="input pl-10"
                      required
                    />
                  </div>
                  {categoryId && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {categoryName}
                    </p>
                  )}

                  {/* Category dropdown */}
                  {showCategoryDropdown && categorySearch.length >= 2 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {isLoadingCategories ? (
                        <div className="p-3 text-center text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                          Searching...
                        </div>
                      ) : categories && categories.length > 0 ? (
                        categories.map((cat) => (
                          <button
                            key={cat.categoryId}
                            type="button"
                            onClick={() => handleSelectCategory(cat)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm"
                          >
                            {cat.categoryName}
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500 text-sm">
                          No categories found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* AI Fill Checkbox - shown when no category selected yet */}
                {!categoryId && (
                  <label className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg cursor-pointer hover:from-purple-100 hover:to-indigo-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={useAiFill}
                      onChange={(e) => setUseAiFill(e.target.checked)}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    <div>
                      <span className="text-sm font-medium text-purple-900">
                        Fill form fields using AI
                      </span>
                      <p className="text-xs text-purple-600">
                        Analyzes your item photos to suggest title, description, and specifics
                      </p>
                    </div>
                  </label>
                )}

                {/* Prompt to select category */}
                {!categoryId && !isLoadingAspects && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      Select a category above to continue
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Category determines available conditions and required fields
                    </p>
                  </div>
                )}

                {/* Loading state */}
                {isLoadingAspects && (
                  <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Loading category requirements...</p>
                  </div>
                )}

                {/* AI Loading state */}
                {isLoadingAiFill && (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 p-6 rounded-lg text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
                      <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                    </div>
                    <p className="text-sm text-purple-700">AI is analyzing your images...</p>
                    <p className="text-xs text-purple-500 mt-1">This may take a few seconds</p>
                  </div>
                )}

                {/* Rest of form - only shown after category is selected and AI is done */}
                {categoryId && !isLoadingAspects && !isLoadingAiFill && (
                  <>
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value.slice(0, 80))}
                        className="input"
                        maxLength={80}
                        required
                        spellCheck={true}
                        autoCorrect="on"
                        autoCapitalize="sentences"
                        autoComplete="one-time-code"
                      />
                      <p className="text-xs text-gray-500 mt-1">{title.length}/80 characters</p>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        className="input"
                        required
                        spellCheck={true}
                        autoCorrect="on"
                        autoCapitalize="sentences"
                        autoComplete="one-time-code"
                      />
                    </div>

                    {/* Condition */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Condition *
                      </label>
                      {categoryConditions.length > 0 && (
                        <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                          <span className="text-xs text-amber-800">
                            Saved in your inventory as <span className="font-semibold capitalize">{item.condition?.replace('_', ' ') || 'unknown'}</span>
                          </span>
                          <span className="text-amber-300">·</span>
                          <span className="text-xs text-amber-700">
                            {categoryConditions.length} eBay options available
                          </span>
                        </div>
                      )}
                      <select
                        value={condition}
                        onChange={(e) => setCondition(Number(e.target.value) as EbayConditionCode)}
                        className="input"
                      >
                        {categoryConditions.length > 0 ? (
                          // Use category-specific conditions
                          categoryConditions.map((c) => (
                            <option key={c.conditionId} value={c.conditionId}>
                              {c.conditionDescription}
                            </option>
                          ))
                        ) : (
                          // Fall back to default conditions
                          Object.entries(EBAY_CONDITIONS).map(([code, label]) => (
                            <option key={code} value={code}>
                              {label}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Item Specifics */}
                    {categoryAspects.length > 0 && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-medium text-blue-900">
                          Item Specifics
                          <span className="font-normal text-blue-700 ml-2">
                            (Required fields marked with *)
                          </span>
                        </h4>
                        <div className="grid grid-cols-1 gap-3">
                          {categoryAspects
                            .sort((a, b) => {
                              // Sort required fields first
                              if (a.aspectRequired && !b.aspectRequired) return -1;
                              if (!a.aspectRequired && b.aspectRequired) return 1;
                              return a.localizedAspectName.localeCompare(b.localizedAspectName);
                            })
                            .slice(0, 15) // Limit to first 15 aspects to avoid overwhelming the form
                            .map((aspect) => (
                              <div key={aspect.localizedAspectName}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {aspect.localizedAspectName}
                                  {aspect.aspectRequired && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                </label>
                                {aspect.aspectMode === 'SELECTION_ONLY' && aspect.aspectValues ? (
                                  <select
                                    value={itemSpecifics[aspect.localizedAspectName] || ''}
                                    onChange={(e) =>
                                      setItemSpecifics((prev) => ({
                                        ...prev,
                                        [aspect.localizedAspectName]: e.target.value,
                                      }))
                                    }
                                    className="input text-sm"
                                  >
                                    <option value="">Select {aspect.localizedAspectName}</option>
                                    {aspect.aspectValues.map((v) => (
                                      <option key={v.localizedValue} value={v.localizedValue}>
                                        {v.localizedValue}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    value={itemSpecifics[aspect.localizedAspectName] || ''}
                                    onChange={(e) =>
                                      setItemSpecifics((prev) => ({
                                        ...prev,
                                        [aspect.localizedAspectName]: e.target.value,
                                      }))
                                    }
                                    placeholder={`Enter ${aspect.localizedAspectName.toLowerCase()}`}
                                    className="input text-sm"
                                  />
                                )}
                              </div>
                            ))}
                        </div>
                        {categoryAspects.length > 15 && (
                          <p className="text-xs text-blue-600">
                            Showing {Math.min(15, categoryAspects.length)} of {categoryAspects.length} fields.
                            Additional fields are optional.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Images preview */}
                    {item.images && item.images.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Images ({item.images.length})
                    </label>
                    <div className="flex gap-2 overflow-x-auto py-1">
                      {item.images.slice(0, 12).map((img, idx) => (
                        <img
                          key={img.id}
                          src={img.thumbnailUrl || img.url}
                          alt={`Image ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      First image will be the gallery image
                    </p>
                  </div>
                )}

                    {/* Location */}
                    {(item.location || item.locationImage) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          Location
                        </label>
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          {item.locationImage && (
                            <img
                              src={item.locationImage}
                              alt="Location"
                              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          {item.location && (
                            <span className="text-sm text-gray-700">{item.location}</span>
                          )}
                        </div>
                      </div>
                    )}

                <hr className="my-4" />

                {/* Listing Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Listing Type *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="listingType"
                        value="AUCTION"
                        checked={listingType === 'AUCTION'}
                        onChange={() => setListingType('AUCTION')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">Auction</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="listingType"
                        value="FIXED_PRICE"
                        checked={listingType === 'FIXED_PRICE'}
                        onChange={() => setListingType('FIXED_PRICE')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">Fixed Price</span>
                    </label>
                  </div>
                </div>

                {/* Pricing */}
                {listingType === 'AUCTION' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Starting Price ({getCurrency()}) *
                      </label>
                      <input
                        type="number"
                        value={startingPrice}
                        onChange={(e) => setStartingPrice(e.target.value)}
                        min="0.01"
                        step="0.01"
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration *
                      </label>
                      <select
                        value={duration}
                        onChange={(e) => setDuration(e.target.value as EbayDuration)}
                        className="input"
                      >
                        {EBAY_DURATIONS.filter((d) => d.value !== 'GTC').map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price ({getCurrency()}) *
                      </label>
                      <input
                        type="number"
                        value={buyItNowPrice}
                        onChange={(e) => setBuyItNowPrice(e.target.value)}
                        min="0.01"
                        step="0.01"
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration *
                      </label>
                      <select
                        value={duration}
                        onChange={(e) => setDuration(e.target.value as EbayDuration)}
                        className="input"
                      >
                        {EBAY_DURATIONS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                  </>
                )}

                {/* Error */}
                {createListingMutation.isError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      {createListingMutation.error instanceof Error
                        ? createListingMutation.error.message
                        : 'Failed to create listing'}
                    </span>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Footer */}
          {!listingResult && (
            <div className="flex gap-3 p-4 border-t sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid || createListingMutation.isPending}
                className="btn-primary flex-1"
              >
                {createListingMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Publishing...
                  </>
                ) : (
                  'Publish to eBay'
                )}
              </button>
            </div>
          )}

          {listingResult && (
            <div className="p-4 border-t">
              <button onClick={handleClose} className="btn-secondary w-full">
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Item Modal */}
      <ItemEditModal
        itemId={item.id}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          // Refresh item data when edit modal closes
          queryClient.invalidateQueries({ queryKey: ['item', item.id] });
        }}
      />
    </div>
  );
}
